// src/contexts/TenantContext.js

import React, { createContext, useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const defaultConfig = {
    logo_url: '',
    welcome_message: 'Sign in',
    tagline: '',
    navLink: {
        text: 'EmpowerAI',
        url: '/dashboard'
    }
};

const TenantContext = createContext({ 
    tenantConfig: defaultConfig, 
    loading: true, 
    setTenant: () => {} 
});

export const TenantProvider = ({ children }) => {
    const [tenantConfig, setTenant] = useState(defaultConfig); 
    const [loading, setLoading] = useState(true);
    const { authFetch, isAuthenticated, isLoading: isAuthLoading } = useAuth();
    
    const location = useLocation();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tenantIdFromState = location.state?.tenantId;
        const tenantIdFromQuery = searchParams.get('tenantId');
        // Prioritize explicit URL/State context
        const publicTenantId = tenantIdFromState || tenantIdFromQuery;

        if (isAuthLoading) {
            setLoading(true);
            return;
        }

        // [PRAXIS FIX] Logic Tree for Branding Source
        // PRIORITY 1: Explicit Context (URL/State).
        // If a specific tenant ID is in the URL, we MUST show that branding,
        // regardless of whether the user is logged in or not.
        if (publicTenantId && authFetch) {
            setLoading(true);
            
            // Use the public endpoint with the explicit ID
            const publicBrandingUrl = `/api/public/branding/?tenant_id=${publicTenantId}`;
            
            // Pass tenantId in options to force authFetch to use it for this call
            authFetch(publicBrandingUrl, { method: 'GET' }, { isPublic: true, tenantId: publicTenantId })
                .then(data => {
                    if (data && data.logo_url !== undefined) {
                        setTenant(prev => ({ ...prev, ...data }));
                    } else {
                        setTenant(defaultConfig);
                    }
                })
                .catch(error => {
                    console.error(`Branding fetch error for tenant ${publicTenantId}:`, error.message);
                    setTenant(defaultConfig);
                })
                .finally(() => setLoading(false));

        } 
        // PRIORITY 2: Authenticated Session Default.
        // No URL context, so show the branding of the user's selected session tenant.
        else if (isAuthenticated && authFetch) {
            setLoading(true);
            authFetch('/api/branding/')
                .then(data => {
                    if (data && data.logo_url !== undefined) {
                        setTenant(prev => ({ ...prev, ...data }));
                    } else {
                        setTenant(defaultConfig);
                    }
                })
                .catch(error => {
                    console.error("Authenticated branding fetch error:", error.message);
                    setTenant(defaultConfig);
                })
                .finally(() => setLoading(false));

        } 
        // PRIORITY 3: Default/Fallback
        else {
            setTenant(defaultConfig);
            setLoading(false);
        }

    }, [authFetch, isAuthenticated, isAuthLoading, location.search, location.state]);

    const value = { tenantConfig, loading, setTenant }; 

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};

export const useTenant = () => {
    return useContext(TenantContext);
};